// src/components/ClientsScreen.jsx
// Cadastro/edição de cliente.
// Rotas:
// - /clients/new
// - /clients/edit  state { clientData }
//
// Backend:
// POST /psy/client
// PUT  /psy/client/:id
//
// O usuário autenticado é identificado exclusivamente pelo Bearer token.
// Não envia id do psicólogo na query/body.
// O envio do termo, quando aplicável, continua sendo responsabilidade do backend.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";

import {
  AlertTriangle,
  ArrowLeft,
  CalendarDays,
  CheckCircle2,
  ContactRound,
  Globe2,
  HeartHandshake,
  LoaderCircle,
  Mail,
  MapPin,
  Menu,
  Phone,
  Plus,
  Save,
  ShieldCheck,
  Trash2,
  UserRound,
  UsersRound,
  X,
} from "lucide-react";
import {
  useLocation,
  useNavigate,
} from "react-router-dom";

import AppSidebar from "../components/AppSidebar";
import { useToast } from "../context/ToastContext";
import Logo from "../assets/logoClaro.webp";

import "../sass/pages/clients.scss";

const API_BASE = (
  process.env.REACT_APP_API_URL ||
  "https://api.psyassist.com.br"
).replace(/\/$/, "");

const AUTH_KEYS = {
  TOKEN: "psy_token",
  USER_NAME: "psy_usuario",
};

function readAuth(key) {
  return (
    sessionStorage.getItem(key) ||
    localStorage.getItem(key) ||
    ""
  );
}

function saveAuthToken(token) {
  if (!token) return;

  localStorage.setItem(
    AUTH_KEYS.TOKEN,
    token
  );

  sessionStorage.setItem(
    AUTH_KEYS.TOKEN,
    token
  );
}

async function readApiPayload(response) {
  try {
    const contentType =
      response.headers.get(
        "content-type"
      ) || "";

    if (
      contentType.includes(
        "application/json"
      )
    ) {
      const data =
        await response.json();

      return data &&
        typeof data === "object"
        ? data
        : {
            message:
              String(
                data ||
                  `Erro HTTP ${response.status}`
              ),
          };
    }

    const text =
      await response.text();

    return {
      message:
        text ||
        `Erro HTTP ${response.status}`,
    };
  } catch {
    return {
      message:
        `Erro HTTP ${response.status}`,
    };
  }
}

function onlyDigits(value = "") {
  return String(value || "")
    .replace(/\D/g, "");
}

function normalizeMultiline(
  value = ""
) {
  return String(value || "")
    .replace(/\r\n/g, "\n")
    .split("\n")
    .map((line) =>
      line
        .replace(/[ \t]+/g, " ")
        .trim()
    )
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function normalizeSingleLine(
  value = ""
) {
  return String(value || "")
    .replace(/\s+/g, " ")
    .trim();
}

function isValidEmail(value) {
  const email =
    String(value || "").trim();

  if (!email) return false;

  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
    email
  );
}

function formatPhoneBR(value) {
  const digits =
    onlyDigits(value).slice(0, 11);

  if (!digits) return "";

  if (digits.length <= 2) {
    return digits;
  }

  if (digits.length <= 6) {
    return `(${digits.slice(
      0,
      2
    )}) ${digits.slice(2)}`;
  }

  if (digits.length <= 10) {
    return `(${digits.slice(
      0,
      2
    )}) ${digits.slice(
      2,
      6
    )}-${digits.slice(6)}`;
  }

  return `(${digits.slice(
    0,
    2
  )}) ${digits.slice(
    2,
    7
  )}-${digits.slice(7)}`;
}

function formatCPF(value) {
  const digits =
    onlyDigits(value).slice(0, 11);

  if (!digits) return "";

  if (digits.length <= 3) {
    return digits;
  }

  if (digits.length <= 6) {
    return `${digits.slice(
      0,
      3
    )}.${digits.slice(3)}`;
  }

  if (digits.length <= 9) {
    return `${digits.slice(
      0,
      3
    )}.${digits.slice(
      3,
      6
    )}.${digits.slice(6)}`;
  }

  return `${digits.slice(
    0,
    3
  )}.${digits.slice(
    3,
    6
  )}.${digits.slice(
    6,
    9
  )}-${digits.slice(9)}`;
}

function formatDocument(value) {
  const digits =
    onlyDigits(value).slice(0, 14);

  if (digits.length <= 11) {
    return formatCPF(digits);
  }

  return `${digits.slice(
    0,
    2
  )}.${digits.slice(
    2,
    5
  )}.${digits.slice(
    5,
    8
  )}/${digits.slice(
    8,
    12
  )}-${digits.slice(12)}`;
}

function formatCEP(value) {
  const digits =
    onlyDigits(value).slice(0, 8);

  if (digits.length <= 5) {
    return digits;
  }

  return `${digits.slice(
    0,
    5
  )}-${digits.slice(5)}`;
}

function dateForInput(value) {
  if (!value) return "";

  const raw =
    String(value).trim();

  const isoDate =
    raw.match(
      /^(\d{4})-(\d{2})-(\d{2})/
    );

  if (isoDate) {
    return `${isoDate[1]}-${isoDate[2]}-${isoDate[3]}`;
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "";
  }

  const year =
    date.getFullYear();

  const month =
    String(
      date.getMonth() + 1
    ).padStart(2, "0");

  const day =
    String(
      date.getDate()
    ).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function todayForInput() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(
    now.getMonth() + 1
  ).padStart(2, "0");
  const day = String(
    now.getDate()
  ).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function createEmergencyContact(
  source = {}
) {
  return {
    key:
      source?._id ||
      source?.key ||
      `${Date.now()}-${Math.random()
        .toString(36)
        .slice(2)}`,

    nome:
      source?.nome || "",

    telefone:
      source?.telefone || "",

    parentesco:
      source?.parentesco || "",

    email:
      source?.email || "",

    observacao:
      source?.observacao || "",
  };
}

function buildInitialForm(client) {
  const source =
    client || {};

  const contacts =
    Array.isArray(
      source.contatosEmergencia
    ) &&
    source.contatosEmergencia.length
      ? source.contatosEmergencia.map(
          createEmergencyContact
        )
      : [];

  return {
    nome:
      source.nome || "",

    email:
      source.email || "",

    telefone:
      source.telefone || "",

    clienteEstrangeiro:
      source.clienteEstrangeiro ===
      true,

    dataNascimento:
      dateForInput(
        source.dataNascimento
      ),

    cpf:
      source.cpf || "",

    dataInicioTerapia:
      dateForInput(
        source.dataInicioTerapia
      ),

    dataFimTerapia:
      dateForInput(
        source.dataFimTerapia
      ),

    cep:
      source.cep || "",

    endereco:
      source.endereco || "",

    bairro:
      source.bairro || "",

    cidade:
      source.cidade || "",

    estado:
      source.estado || "",

    menorDeIdade:
      source.menorDeIdade === true,

    cpfResponsavel:
      source.cpfResponsavel || "",

    pagadorEhCliente:
      source.pagadorEhCliente !==
      false,

    cpfPagador:
      source.cpfPagador || "",

    atendimentoCasal:
      source.atendimentoCasal === true,

    conjuge: {
      nome:
        source?.conjuge?.nome || "",
      tipo:
        source?.conjuge?.tipo || "",
      cpf:
        source?.conjuge?.cpf || "",
    },

    observacoes:
      source.observacoes || "",

    contatosEmergencia:
      contacts,

    ativo:
      source.ativo !== false,
  };
}

function Field({
  label,
  required = false,
  hint = "",
  error = "",
  className = "",
  children,
}) {
  return (
    <div
      className={`cform-field ${className} ${
        error
          ? "cform-field--error"
          : ""
      }`}
    >
      <div className="cform-field__top">
        <label className="cform-label">
          {label}

          {required ? (
            <span className="cform-required">
              *
            </span>
          ) : null}
        </label>

        {hint ? (
          <span className="cform-hint">
            {hint}
          </span>
        ) : null}
      </div>

      {children}

      {error ? (
        <div
          className="cform-error"
          role="alert"
        >
          {error}
        </div>
      ) : null}
    </div>
  );
}

function ToggleCard({
  checked,
  onChange,
  title,
  description,
  icon,
  disabled = false,
}) {
  return (
    <button
      type="button"
      className={`cform-toggle-card ${
        checked
          ? "cform-toggle-card--active"
          : ""
      }`}
      onClick={() =>
        !disabled &&
        onChange(!checked)
      }
      disabled={disabled}
      aria-pressed={checked}
    >
      <span className="cform-toggle-card__icon">
        {icon}
      </span>

      <span className="cform-toggle-card__copy">
        <strong>{title}</strong>
        <small>
          {description}
        </small>
      </span>

      <span
        className={`cform-switch ${
          checked
            ? "cform-switch--active"
            : ""
        }`}
        aria-hidden="true"
      >
        <i />
      </span>
    </button>
  );
}

export default function ClientsScreen() {
  const navigate =
    useNavigate();

  const location =
    useLocation();

  const toast =
    useToast();

  const pageRef =
    useRef(null);

  const editingData =
    location.state?.clientData ||
    null;

  const isEdit =
    Boolean(
      editingData?._id ||
      editingData?.id
    );

  const clientId =
    editingData?._id ||
    editingData?.id ||
    "";

  const originalTherapyEnd =
    useMemo(
      () =>
        dateForInput(
          editingData?.dataFimTerapia
        ),
      [editingData?.dataFimTerapia]
    );

  const cycleAlreadyEnded =
    Boolean(
      isEdit &&
      originalTherapyEnd
    );

  const [sidebarOpen, setSidebarOpen] =
    useState(false);

  const [saving, setSaving] =
    useState(false);

  const [cycleDialog, setCycleDialog] =
    useState(null);

  const [cycleClosing, setCycleClosing] =
    useState(false);

  const [cycleReason, setCycleReason] =
    useState("");

  const [newCycleDialog, setNewCycleDialog] =
    useState(false);

  const [newCycleStartDate, setNewCycleStartDate] =
    useState("");

  const [startingNewCycle, setStartingNewCycle] =
    useState(false);

  const [buscandoCep, setBuscandoCep] =
    useState(false);

  const [cepEncontrado, setCepEncontrado] =
    useState(false);

  const lastCepRequestedRef =
    useRef("");

  const [errors, setErrors] =
    useState({});

  const [form, setForm] =
    useState(() =>
      buildInitialForm(
        editingData
      )
    );

  const userName =
    useMemo(
      () =>
        readAuth(
          AUTH_KEYS.USER_NAME
        ) || "Usuário",
      []
    );

  useEffect(() => {
    if (
      location.pathname ===
        "/clients/edit" &&
      !isEdit
    ) {
      toast.warning({
        title:
          "Cliente não identificado",
        message:
          "Abra o cadastro a partir da tela de detalhes para editar um cliente.",
        duration: 5200,
      });

      navigate(
        "/clients",
        {
          replace: true,
        }
      );
    }
  }, [
    isEdit,
    location.pathname,
    navigate,
    toast,
  ]);

  const setField =
    (
      name,
      value
    ) => {
      setForm(
        (current) => ({
          ...current,
          [name]: value,
        })
      );

      if (errors[name]) {
        setErrors(
          (current) => ({
            ...current,
            [name]: "",
          })
        );
      }
    };

  const setConjugeField =
    (
      name,
      value
    ) => {
      setForm(
        (current) => ({
          ...current,
          conjuge: {
            ...(current.conjuge || {}),
            [name]: value,
          },
        })
      );

      const errorKey = {
        nome: "conjugeNome",
        tipo: "conjugeTipo",
        cpf: "conjugeCpf",
      }[name];

      if (
        errorKey &&
        errors[errorKey]
      ) {
        setErrors(
          (current) => ({
            ...current,
            [errorKey]: "",
          })
        );
      }
    };

  const setAtendimentoCasal =
    (value) => {
      if (
        !value &&
        form.atendimentoCasal &&
        isEdit &&
        (
          normalizeSingleLine(
            form?.conjuge?.nome
          ) ||
          normalizeSingleLine(
            form?.conjuge?.tipo
          ) ||
          normalizeSingleLine(
            form?.conjuge?.cpf
          )
        )
      ) {
        const confirmed =
          window.confirm(
            "Alterar para atendimento individual? Ao salvar, os dados do parceiro(a) serão removidos deste acompanhamento."
          );

        if (!confirmed) {
          return;
        }
      }

      setField(
        "atendimentoCasal",
        value
      );

      if (!value) {
        setErrors(
          (current) => ({
            ...current,
            conjugeNome: "",
            conjugeTipo: "",
            conjugeCpf: "",
          })
        );
      }
    };

  const buscarEnderecoPorCep =
    useCallback(
      async (rawCep) => {
        if (
          form.clienteEstrangeiro
        ) {
          return;
        }

        const digits =
          onlyDigits(rawCep);

        if (
          digits.length !== 8
        ) {
          return;
        }

        if (
          lastCepRequestedRef.current ===
          digits
        ) {
          return;
        }

        lastCepRequestedRef.current =
          digits;

        setBuscandoCep(true);
        setCepEncontrado(false);

        try {
          const response =
            await axios.get(
              `https://viacep.com.br/ws/${digits}/json/`,
              {
                timeout: 8000,
              }
            );

          if (
            response.status === 200 &&
            !response.data?.erro
          ) {
            /*
             * Se o usuário já digitou outro CEP enquanto
             * a consulta estava em andamento, descartamos
             * a resposta antiga.
             */
            if (
              lastCepRequestedRef.current !==
              digits
            ) {
              return;
            }

            const logradouro =
              String(
                response.data?.logradouro ||
                  ""
              ).trim();

            const bairro =
              String(
                response.data?.bairro ||
                  ""
              ).trim();

            const cidade =
              String(
                response.data?.localidade ||
                  ""
              ).trim();

            const estado =
              String(
                response.data?.uf ||
                  ""
              )
                .trim()
                .toUpperCase();

            setForm(
              (current) => ({
                ...current,

                /*
                 * O número/complemento continua editável.
                 * O ViaCEP preenche apenas o logradouro
                 * disponível no CEP.
                 */
                endereco:
                  logradouro ||
                  current.endereco,

                bairro:
                  bairro ||
                  current.bairro,

                cidade:
                  cidade ||
                  current.cidade,

                estado:
                  estado ||
                  current.estado,
              })
            );

            setErrors(
              (current) => ({
                ...current,
                cep: "",
                endereco:
                  logradouro
                    ? ""
                    : current.endereco,
                bairro:
                  bairro
                    ? ""
                    : current.bairro,
                cidade:
                  cidade
                    ? ""
                    : current.cidade,
                estado:
                  estado
                    ? ""
                    : current.estado,
              })
            );

            setCepEncontrado(true);
            return;
          }

          setCepEncontrado(false);

          setErrors(
            (current) => ({
              ...current,
              cep:
                "CEP não localizado. Revise o número informado.",
            })
          );
        } catch (error) {
          console.error(
            "Erro ao consultar CEP:",
            error
          );

          /*
           * Permite nova tentativa caso o usuário
           * apague/reinforme o mesmo CEP.
           */
          lastCepRequestedRef.current =
            "";

          setCepEncontrado(false);

          toast.warning({
            title:
              "Consulta de CEP indisponível",
            message:
              "Você pode continuar preenchendo o endereço manualmente.",
          });
        } finally {
          setBuscandoCep(false);
        }
      },
      [
        form.clienteEstrangeiro,
        toast,
      ]
    );

  useEffect(() => {
    if (
      form.clienteEstrangeiro
    ) {
      lastCepRequestedRef.current =
        "";

      setBuscandoCep(false);
      setCepEncontrado(false);

      return undefined;
    }

    const digits =
      onlyDigits(form.cep);

    if (
      digits.length !== 8
    ) {
      lastCepRequestedRef.current =
        "";

      setCepEncontrado(false);

      return undefined;
    }

    const timeoutId =
      window.setTimeout(
        () => {
          buscarEnderecoPorCep(
            digits
          );
        },
        450
      );

    return () =>
      window.clearTimeout(
        timeoutId
      );
  }, [
    buscarEnderecoPorCep,
    form.cep,
    form.clienteEstrangeiro,
  ]);

  const updateEmergencyContact =
    (
      index,
      field,
      value
    ) => {
      setForm(
        (current) => ({
          ...current,

          contatosEmergencia:
            current.contatosEmergencia.map(
              (
                contact,
                contactIndex
              ) =>
                contactIndex ===
                index
                  ? {
                      ...contact,
                      [field]:
                        value,
                    }
                  : contact
            ),
        })
      );
    };

  const addEmergencyContact =
    () => {
      setForm(
        (current) => ({
          ...current,

          contatosEmergencia: [
            ...current.contatosEmergencia,
            createEmergencyContact(),
          ],
        })
      );
    };

  const removeEmergencyContact =
    (index) => {
      setForm(
        (current) => ({
          ...current,

          contatosEmergencia:
            current.contatosEmergencia.filter(
              (
                _,
                contactIndex
              ) =>
                contactIndex !==
                index
            ),
        })
      );
    };

  const completion =
    useMemo(() => {
      const checks = [
        Boolean(
          normalizeSingleLine(
            form.nome
          )
        ),
        Boolean(
          form.dataInicioTerapia
        ),
      ];

      if (
        !form.clienteEstrangeiro
      ) {
        checks.push(
          isValidEmail(
            form.email
          )
        );

        checks.push(
          [
            10,
            11,
          ].includes(
            onlyDigits(
              form.telefone
            ).length
          )
        );

        checks.push(
          Boolean(
            normalizeSingleLine(
              form.endereco
            )
          )
        );

        checks.push(
          Boolean(
            normalizeSingleLine(
              form.bairro
            )
          )
        );

        checks.push(
          Boolean(
            normalizeSingleLine(
              form.cidade
            )
          )
        );

        checks.push(
          /^[A-Za-z]{2}$/.test(
            String(
              form.estado || ""
            ).trim()
          )
        );
      }

      const valid =
        checks.filter(Boolean)
          .length;

      return Math.round(
        (valid /
          Math.max(
            checks.length,
            1
          )) *
          100
      );
    }, [form]);

  const validate =
    () => {
      const next = {};

      if (
        !normalizeSingleLine(
          form.nome
        )
      ) {
        next.nome =
          "Informe o nome do cliente.";
      }

      if (
        !form.clienteEstrangeiro
      ) {
        if (
          !isValidEmail(
            form.email
          )
        ) {
          next.email =
            "Informe um e-mail válido.";
        }

        const phoneDigits =
          onlyDigits(
            form.telefone
          );

        if (
          ![
            10,
            11,
          ].includes(
            phoneDigits.length
          )
        ) {
          next.telefone =
            "Informe um telefone com DDD.";
        }

        if (
          !normalizeSingleLine(
            form.endereco
          )
        ) {
          next.endereco =
            "Informe o endereço.";
        }

        if (
          !normalizeSingleLine(
            form.bairro
          )
        ) {
          next.bairro =
            "Informe o bairro.";
        }

        if (
          !normalizeSingleLine(
            form.cidade
          )
        ) {
          next.cidade =
            "Informe a cidade.";
        }

        if (
          !/^[A-Za-z]{2}$/.test(
            String(
              form.estado || ""
            ).trim()
          )
        ) {
          next.estado =
            "Informe a UF com 2 letras.";
        }

        const cepDigits =
          onlyDigits(
            form.cep
          );

        if (
          form.cep &&
          cepDigits.length !== 8
        ) {
          next.cep =
            "Informe um CEP válido.";
        }
      }

      if (
        form.dataFimTerapia &&
        form.dataInicioTerapia &&
        form.dataFimTerapia <
          form.dataInicioTerapia
      ) {
        next.dataFimTerapia =
          "A data de fim não pode ser anterior ao início da terapia.";
      }

      if (
        form.email &&
        !isValidEmail(
          form.email
        )
      ) {
        next.email =
          "Informe um e-mail válido.";
      }

      if (
        form.cpf &&
        !form.clienteEstrangeiro &&
        onlyDigits(
          form.cpf
        ).length !== 11
      ) {
        next.cpf =
          "Informe um CPF com 11 dígitos.";
      }

      if (
        form.cpfResponsavel &&
        onlyDigits(
          form.cpfResponsavel
        ).length !== 11
      ) {
        next.cpfResponsavel =
          "Informe um CPF válido.";
      }

      if (
        !form.pagadorEhCliente &&
        form.cpfPagador
      ) {
        const payerDigits =
          onlyDigits(
            form.cpfPagador
          );

        if (
          ![
            11,
            14,
          ].includes(
            payerDigits.length
          )
        ) {
          next.cpfPagador =
            "Informe CPF ou CNPJ válido.";
        }
      }

      if (form.atendimentoCasal) {
        if (
          !normalizeSingleLine(
            form?.conjuge?.nome
          )
        ) {
          next.conjugeNome =
            "Informe o nome do parceiro(a).";
        }

        if (
          !normalizeSingleLine(
            form?.conjuge?.tipo
          )
        ) {
          next.conjugeTipo =
            "Informe o vínculo do parceiro(a).";
        }

        if (
          form?.conjuge?.cpf &&
          onlyDigits(
            form.conjuge.cpf
          ).length !== 11
        ) {
          next.conjugeCpf =
            "Informe um CPF válido com 11 dígitos.";
        }
      }

      const invalidContact =
        form.contatosEmergencia.findIndex(
          (contact) =>
            contact.email &&
            !isValidEmail(
              contact.email
            )
        );

      if (
        invalidContact >= 0
      ) {
        next.contatosEmergencia =
          `O e-mail do contato ${
            invalidContact + 1
          } é inválido.`;
      }

      setErrors(next);

      return (
        Object.keys(next).length ===
        0
      );
    };

  const buildPayload =
    () => {
      const contacts =
        form.contatosEmergencia
          .map((contact) => ({
            nome:
              normalizeSingleLine(
                contact.nome
              ),

            telefone:
              normalizeSingleLine(
                contact.telefone
              ),

            parentesco:
              normalizeSingleLine(
                contact.parentesco
              ),

            email:
              String(
                contact.email || ""
              )
                .trim()
                .toLowerCase(),

            observacao:
              normalizeSingleLine(
                contact.observacao
              ),
          }))
          .filter((contact) =>
            Object.values(
              contact
            ).some(Boolean)
          );

      const payload = {
        nome:
          normalizeSingleLine(
            form.nome
          ),

        email:
          String(
            form.email || ""
          )
            .trim()
            .toLowerCase(),

        telefone:
          normalizeSingleLine(
            form.telefone
          ),

        clienteEstrangeiro:
          Boolean(
            form.clienteEstrangeiro
          ),

        dataNascimento:
          form.dataNascimento ||
          null,

        dataInicioTerapia:
          form.dataInicioTerapia ||
          null,

        dataFimTerapia:
          form.dataFimTerapia ||
          null,

        cpf:
          normalizeSingleLine(
            form.cpf
          ),

        cep:
          normalizeSingleLine(
            form.cep
          ),

        endereco:
          normalizeSingleLine(
            form.endereco
          ),

        bairro:
          normalizeSingleLine(
            form.bairro
          ),

        cidade:
          normalizeSingleLine(
            form.cidade
          ),

        estado:
          String(
            form.estado || ""
          )
            .trim()
            .toUpperCase(),

        menorDeIdade:
          Boolean(
            form.menorDeIdade
          ),

        pagadorEhCliente:
          Boolean(
            form.pagadorEhCliente
          ),

        atendimentoCasal:
          Boolean(
            form.atendimentoCasal
          ),

        conjuge:
          form.atendimentoCasal
            ? {
                nome:
                  normalizeSingleLine(
                    form?.conjuge?.nome
                  ),
                tipo:
                  normalizeSingleLine(
                    form?.conjuge?.tipo
                  ),
                cpf:
                  form?.conjuge?.cpf
                    ? formatCPF(
                        form.conjuge.cpf
                      )
                    : null,
              }
            : null,

        observacoes:
          normalizeMultiline(
            form.observacoes
          ),

        contatosEmergencia:
          contacts,

        ativo:
          Boolean(
            form.ativo
          ),
      };

      if (
        form.cpfResponsavel
      ) {
        payload.cpfResponsavel =
          normalizeSingleLine(
            form.cpfResponsavel
          );
      } else {
        payload.cpfResponsavel =
          null;
      }

      if (
        form.pagadorEhCliente
      ) {
        /*
         * O controller atual já usa o CPF do cliente como fallback
         * quando cpfPagador não é informado.
         */
        payload.cpfPagador =
          "";
      } else if (
        form.cpfPagador
      ) {
        payload.cpfPagador =
          normalizeSingleLine(
            form.cpfPagador
          );
      }

      return payload;
    };

  const handleSave =
    async () => {
      if (
        saving ||
        !validate()
      ) {
        return;
      }

      const token =
        readAuth(
          AUTH_KEYS.TOKEN
        );

      if (!token) {
        toast.warning({
          title:
            "Sessão expirada",
          message:
            "Entre novamente para salvar o cliente.",
          duration: 5200,
        });

        navigate(
          "/login",
          {
            replace: true,
          }
        );

        return;
      }

      setSaving(true);

      try {
        const payload =
          buildPayload();

        const isNewTherapyCycleEnd =
          Boolean(
            isEdit &&
            payload.dataFimTerapia &&
            payload.dataFimTerapia !==
              originalTherapyEnd
          );

        if (isNewTherapyCycleEnd) {
          const impactResponse =
            await fetch(
              `${API_BASE}/psy/client/${encodeURIComponent(
                clientId
              )}/ciclo-terapeutico/impacto`,
              {
                method: "POST",
                headers: {
                  Authorization:
                    `Bearer ${token}`,
                  "Content-Type":
                    "application/json",
                  Accept:
                    "application/json",
                },
                body: JSON.stringify({
                  dataFimTerapia:
                    payload.dataFimTerapia,
                }),
              }
            );

          const impactToken =
            impactResponse.headers.get(
              "x-new-token"
            );

          if (impactToken) {
            saveAuthToken(impactToken);
          }

          const impactData =
            await readApiPayload(
              impactResponse
            );

          if (
            impactResponse.status === 401
          ) {
            toast.warning({
              title: "Sessão expirada",
              message:
                impactData?.message ||
                "Entre novamente para continuar.",
              duration: 5200,
            });

            navigate(
              "/login",
              { replace: true }
            );

            return;
          }

          if (!impactResponse.ok) {
            throw new Error(
              impactData?.message ||
                "Não foi possível calcular o impacto do encerramento."
            );
          }

          setCycleReason("");
          setCycleDialog({
            payload,
            impacto: impactData,
          });

          return;
        }

        const endpoint =
          isEdit
            ? `${API_BASE}/psy/client/${encodeURIComponent(
                clientId
              )}`
            : `${API_BASE}/psy/client`;

        const response =
          await fetch(
            endpoint,
            {
              method:
                isEdit
                  ? "PUT"
                  : "POST",

              headers: {
                Authorization:
                  `Bearer ${token}`,

                "Content-Type":
                  "application/json",

                Accept:
                  "application/json",
              },

              body:
                JSON.stringify(
                  payload
                ),
            }
          );

        const newToken =
          response.headers.get(
            "x-new-token"
          );

        if (newToken) {
          saveAuthToken(
            newToken
          );
        }

        const data =
          await readApiPayload(
            response
          );

        if (
          response.status === 401
        ) {
          toast.warning({
            title:
              "Sessão expirada",
            message:
              data?.message ||
              "Entre novamente para continuar.",
            duration: 5200,
          });

          navigate(
            "/login",
            {
              replace: true,
            }
          );

          return;
        }

        if (!response.ok) {
          const details =
            Array.isArray(
              data?.detalhes
            )
              ? data.detalhes.join(
                  " "
                )
              : "";

          throw new Error(
            details ||
              data?.message ||
              "Não foi possível salvar o cliente."
          );
        }

        toast.success({
          title:
            isEdit
              ? "Cliente atualizado"
              : "Cliente cadastrado",

          message:
            isEdit
              ? "As informações do cliente foram atualizadas com sucesso."
              : "Cliente cadastrado com sucesso. Os fluxos de comunicação continuam sendo processados pelo backend.",

          duration: 4200,
        });

        if (isEdit) {
          navigate(
            "/clients/details",
            {
              replace: true,

              state: {
                clientData:
                  data?.cliente || {
                    ...editingData,
                    ...payload,
                    _id:
                      clientId,
                  },

                refresh: true,
              },
            }
          );

          return;
        }

        navigate(
          "/clients",
          {
            replace: true,

            state: {
              refresh: true,
            },
          }
        );
      } catch (error) {
        console.error(
          "Erro ao salvar cliente:",
          error
        );

        toast.error({
          title:
            "Não foi possível salvar",
          message:
            error?.message ||
            "Falha ao salvar o cliente.",
          duration: 6500,
        });

        window.requestAnimationFrame(
          () => {
            pageRef.current?.scrollIntoView(
              {
                behavior:
                  "smooth",
                block: "start",
              }
            );
          }
        );
      } finally {
        setSaving(false);
      }
    };

  const confirmTherapyCycleEnd =
    async () => {
      if (
        cycleClosing ||
        !cycleDialog?.payload ||
        !clientId
      ) {
        return;
      }

      const token =
        readAuth(
          AUTH_KEYS.TOKEN
        );

      if (!token) {
        toast.warning({
          title: "Sessão expirada",
          message:
            "Entre novamente para concluir o encerramento.",
          duration: 5200,
        });

        navigate(
          "/login",
          { replace: true }
        );

        return;
      }

      setCycleClosing(true);

      let cadastroAtualizado = false;

      try {
        /*
         * Primeiro salva as demais alterações cadastrais mantendo a data
         * anterior de fim. O encerramento em si ocorre somente na rota
         * específica após esta confirmação explícita.
         */
        const regularPayload = {
          ...cycleDialog.payload,
          dataFimTerapia:
            originalTherapyEnd ||
            null,
        };

        const saveResponse =
          await fetch(
            `${API_BASE}/psy/client/${encodeURIComponent(
              clientId
            )}`,
            {
              method: "PUT",
              headers: {
                Authorization:
                  `Bearer ${token}`,
                "Content-Type":
                  "application/json",
                Accept:
                  "application/json",
              },
              body:
                JSON.stringify(
                  regularPayload
                ),
            }
          );

        const saveToken =
          saveResponse.headers.get(
            "x-new-token"
          );

        if (saveToken) {
          saveAuthToken(saveToken);
        }

        const saveData =
          await readApiPayload(
            saveResponse
          );

        if (saveResponse.status === 401) {
          toast.warning({
            title: "Sessão expirada",
            message:
              saveData?.message ||
              "Entre novamente para continuar.",
            duration: 5200,
          });

          navigate(
            "/login",
            { replace: true }
          );

          return;
        }

        if (!saveResponse.ok) {
          throw new Error(
            saveData?.message ||
              "Não foi possível salvar os dados do cliente."
          );
        }

        cadastroAtualizado = true;

        const endResponse =
          await fetch(
            `${API_BASE}/psy/client/${encodeURIComponent(
              clientId
            )}/ciclo-terapeutico/encerrar`,
            {
              method: "POST",
              headers: {
                Authorization:
                  `Bearer ${token}`,
                "Content-Type":
                  "application/json",
                Accept:
                  "application/json",
              },
              body: JSON.stringify({
                dataFimTerapia:
                  cycleDialog.payload
                    .dataFimTerapia,
                motivo:
                  normalizeMultiline(
                    cycleReason
                  ) || null,
              }),
            }
          );

        const endToken =
          endResponse.headers.get(
            "x-new-token"
          );

        if (endToken) {
          saveAuthToken(endToken);
        }

        const endData =
          await readApiPayload(
            endResponse
          );

        if (endResponse.status === 401) {
          toast.warning({
            title: "Sessão expirada",
            message:
              endData?.message ||
              "Entre novamente para continuar.",
            duration: 5200,
          });

          navigate(
            "/login",
            { replace: true }
          );

          return;
        }

        if (!endResponse.ok) {
          throw new Error(
            endData?.message ||
              "Não foi possível encerrar o ciclo terapêutico."
          );
        }

        const totalCancelado =
          Number(
            endData?.impacto
              ?.totalCancelado || 0
          );

        toast.success({
          title:
            "Ciclo terapêutico encerrado",
          message:
            totalCancelado > 0
              ? `${totalCancelado} agendamento(s) futuro(s) foram cancelados. O histórico clínico e financeiro foi preservado.`
              : "O ciclo foi encerrado. Não havia agendamentos futuros para cancelar.",
          duration: 6200,
        });

        const integrationWarnings =
          Array.isArray(
            endData?.impacto?.avisos
          )
            ? endData.impacto.avisos
            : [];

        if (integrationWarnings.length) {
          toast.warning({
            title:
              "Encerramento concluído com avisos",
            message:
              `${integrationWarnings.length} integração(ões) ou lembrete(s) não puderam ser processados. Os agendamentos permaneceram cancelados no PsyAssist.`,
            duration: 7600,
          });
        }

        setCycleDialog(null);
        setCycleReason("");

        navigate(
          "/clients/details",
          {
            replace: true,
            state: {
              clientData:
                endData?.cliente ||
                saveData?.cliente || {
                  ...editingData,
                  ...regularPayload,
                  dataFimTerapia:
                    cycleDialog.payload
                      .dataFimTerapia,
                  _id: clientId,
                },
              refresh: true,
            },
          }
        );
      } catch (error) {
        console.error(
          "Erro ao encerrar ciclo terapêutico:",
          error
        );

        toast.error({
          title:
            "Não foi possível encerrar o ciclo",
          message:
            cadastroAtualizado
              ? `Os demais dados cadastrais foram salvos, mas o encerramento não foi concluído. ${
                  error?.message || "Tente novamente."
                }`
              : error?.message ||
                "Falha ao encerrar o ciclo terapêutico.",
          duration: 7600,
        });
      } finally {
        setCycleClosing(false);
      }
    };

  const openNewTherapyCycleDialog =
    () => {
      const today =
        todayForInput();

      setNewCycleStartDate(
        originalTherapyEnd &&
          originalTherapyEnd > today
          ? originalTherapyEnd
          : today
      );

      setNewCycleDialog(true);
    };

  const confirmStartNewTherapyCycle =
    async () => {
      if (
        startingNewCycle ||
        !clientId ||
        !newCycleStartDate
      ) {
        return;
      }

      if (!validate()) {
        setNewCycleDialog(false);
        return;
      }

      const token =
        readAuth(
          AUTH_KEYS.TOKEN
        );

      if (!token) {
        toast.warning({
          title: "Sessão expirada",
          message:
            "Entre novamente para iniciar um novo ciclo.",
          duration: 5200,
        });

        navigate(
          "/login",
          { replace: true }
        );

        return;
      }

      setStartingNewCycle(true);

      try {
        /* Salva eventuais alterações cadastrais antes de abrir o novo ciclo. */
        const payload =
          buildPayload();

        const saveResponse =
          await fetch(
            `${API_BASE}/psy/client/${encodeURIComponent(
              clientId
            )}`,
            {
              method: "PUT",
              headers: {
                Authorization:
                  `Bearer ${token}`,
                "Content-Type":
                  "application/json",
                Accept:
                  "application/json",
              },
              body:
                JSON.stringify(
                  payload
                ),
            }
          );

        const saveToken =
          saveResponse.headers.get(
            "x-new-token"
          );

        if (saveToken) {
          saveAuthToken(saveToken);
        }

        const saveData =
          await readApiPayload(
            saveResponse
          );

        if (!saveResponse.ok) {
          throw new Error(
            saveData?.message ||
              "Não foi possível salvar os dados antes de iniciar o novo ciclo."
          );
        }

        const response =
          await fetch(
            `${API_BASE}/psy/client/${encodeURIComponent(
              clientId
            )}/ciclo-terapeutico/iniciar`,
            {
              method: "POST",
              headers: {
                Authorization:
                  `Bearer ${token}`,
                "Content-Type":
                  "application/json",
                Accept:
                  "application/json",
              },
              body: JSON.stringify({
                dataInicioTerapia:
                  newCycleStartDate,
              }),
            }
          );

        const newToken =
          response.headers.get(
            "x-new-token"
          );

        if (newToken) {
          saveAuthToken(newToken);
        }

        const data =
          await readApiPayload(
            response
          );

        if (response.status === 401) {
          toast.warning({
            title: "Sessão expirada",
            message:
              data?.message ||
              "Entre novamente para continuar.",
            duration: 5200,
          });

          navigate(
            "/login",
            { replace: true }
          );
          return;
        }

        if (!response.ok) {
          throw new Error(
            data?.message ||
              "Não foi possível iniciar o novo ciclo terapêutico."
          );
        }

        toast.success({
          title:
            "Novo ciclo terapêutico iniciado",
          message:
            "O ciclo anterior foi preservado no histórico. Agendamentos cancelados anteriormente não foram reativados.",
          duration: 6200,
        });

        setNewCycleDialog(false);

        navigate(
          "/clients/details",
          {
            replace: true,
            state: {
              clientData:
                data?.cliente ||
                saveData?.cliente || {
                  ...editingData,
                  ...payload,
                  dataInicioTerapia:
                    newCycleStartDate,
                  dataFimTerapia: null,
                  _id: clientId,
                },
              refresh: true,
            },
          }
        );
      } catch (error) {
        console.error(
          "Erro ao iniciar novo ciclo terapêutico:",
          error
        );

        toast.error({
          title:
            "Não foi possível iniciar o novo ciclo",
          message:
            error?.message ||
            "Tente novamente.",
          duration: 7000,
        });
      } finally {
        setStartingNewCycle(false);
      }
    };

  const goBack =
    () => {
      if (isEdit) {
        navigate(
          "/clients/details",
          {
            state: {
              clientData:
                editingData,
            },
          }
        );

        return;
      }

      navigate(
        "/clients"
      );
    };

  return (
    <div
      className="cform-page"
      ref={pageRef}
    >
      <div
        className="cform-bg"
        aria-hidden="true"
      >
        <span className="cform-bg__orb cform-bg__orb--one" />
        <span className="cform-bg__orb cform-bg__orb--two" />
        <span className="cform-bg__orb cform-bg__orb--three" />
      </div>

      <AppSidebar
        open={sidebarOpen}
        onClose={() =>
          setSidebarOpen(false)
        }
      />

      <header className="cform-topbar">
        <button
          className="cform-icon-btn"
          type="button"
          onClick={() =>
            setSidebarOpen(true)
          }
          aria-label="Abrir menu"
        >
          <Menu
            size={19}
            aria-hidden="true"
          />
        </button>

        <div className="cform-brand">
          <img
            src={Logo}
            alt="PsyAssist"
            className="cform-brand__logo"
          />

          <div className="cform-brand__text">
            <strong>
              {isEdit
                ? "Editar cliente"
                : "Novo cliente"}
            </strong>

            <span>
              Base clínica •{" "}
              {userName}
            </span>
          </div>
        </div>

        <div className="cform-top-actions">
          <button
            className="cform-btn cform-btn--ghost cform-btn--desktop"
            type="button"
            onClick={goBack}
            disabled={saving}
          >
            <ArrowLeft
              size={16}
              aria-hidden="true"
            />
            Voltar
          </button>

          <button
            className="cform-btn cform-btn--primary"
            type="button"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <LoaderCircle
                  size={16}
                  className="cform-spin"
                  aria-hidden="true"
                />
                Salvando...
              </>
            ) : (
              <>
                <Save
                  size={16}
                  aria-hidden="true"
                />
                {isEdit
                  ? "Salvar alterações"
                  : "Cadastrar"}
              </>
            )}
          </button>
        </div>
      </header>

      <main className="cform-main">
        <section className="cform-hero">
          <div>
            <span className="cform-kicker">
              <UsersRound
                size={15}
                aria-hidden="true"
              />
              Gestão clínica
            </span>

            <h1>
              {isEdit
                ? "Atualize o cadastro do cliente"
                : "Cadastre um novo cliente"}
            </h1>

            <p>
              Organize dados pessoais, contatos, ciclo terapêutico,
              responsável financeiro e contatos de emergência em um cadastro
              único.
            </p>
          </div>

          <div className="cform-progress-card">
            <small>
              Dados essenciais
            </small>

            <strong>
              {completion}%
            </strong>

            <div className="cform-progress-card__track">
              <span
                style={{
                  width:
                    `${completion}%`,
                }}
              />
            </div>

            <p>
              O fim do ciclo terapêutico é opcional e não inativa o cliente
              automaticamente.
            </p>
          </div>
        </section>

        <section className="cform-card">
          <div className="cform-card__head">
            <span className="cform-card__icon">
              <UserRound
                size={20}
                aria-hidden="true"
              />
            </span>

            <div>
              <h2>
                Identificação
              </h2>
              <p>
                Dados básicos do cliente para prontuário e comunicação.
              </p>
            </div>
          </div>

          <div className="cform-grid">
            <Field
              label="Nome completo"
              required
              error={errors.nome}
              className="cform-span-8"
            >
              <input
                className="cform-input"
                value={form.nome}
                onChange={(event) =>
                  setField(
                    "nome",
                    event.target.value
                  )
                }
                placeholder="Nome do cliente"
                autoComplete="name"
              />
            </Field>

            <Field
              label="Data de nascimento"
              className="cform-span-4"
            >
              <input
                className="cform-input"
                type="date"
                value={
                  form.dataNascimento
                }
                onChange={(event) =>
                  setField(
                    "dataNascimento",
                    event.target.value
                  )
                }
              />
            </Field>

            <Field
              label="E-mail"
              required={
                !form.clienteEstrangeiro
              }
              error={errors.email}
              className="cform-span-6"
              hint={
                form.clienteEstrangeiro
                  ? "Opcional para cliente estrangeiro"
                  : "Usado em comunicações"
              }
            >
              <div className="cform-input-icon">
                <Mail
                  size={17}
                  aria-hidden="true"
                />

                <input
                  type="email"
                  value={form.email}
                  onChange={(event) =>
                    setField(
                      "email",
                      event.target.value
                    )
                  }
                  placeholder="cliente@email.com"
                  autoComplete="email"
                />
              </div>
            </Field>

            <Field
              label="Telefone"
              required={
                !form.clienteEstrangeiro
              }
              error={errors.telefone}
              className="cform-span-6"
            >
              <div className="cform-input-icon">
                <Phone
                  size={17}
                  aria-hidden="true"
                />

                <input
                  inputMode="tel"
                  value={
                    form.telefone
                  }
                  onChange={(event) =>
                    setField(
                      "telefone",
                      form.clienteEstrangeiro
                        ? event.target.value
                        : formatPhoneBR(
                            event.target.value
                          )
                    )
                  }
                  placeholder={
                    form.clienteEstrangeiro
                      ? "Telefone internacional"
                      : "(11) 99999-9999"
                  }
                  autoComplete="tel"
                />
              </div>
            </Field>

            <Field
              label="CPF"
              error={errors.cpf}
              className="cform-span-6"
              hint={
                form.clienteEstrangeiro
                  ? "Opcional"
                  : ""
              }
            >
              <input
                className="cform-input"
                inputMode="numeric"
                value={form.cpf}
                onChange={(event) =>
                  setField(
                    "cpf",
                    form.clienteEstrangeiro
                      ? event.target.value
                      : formatCPF(
                          event.target.value
                        )
                  )
                }
                placeholder="000.000.000-00"
              />
            </Field>

            <div className="cform-span-6 cform-toggle-wrap">
              <ToggleCard
                checked={
                  form.clienteEstrangeiro
                }
                onChange={(value) =>
                  setField(
                    "clienteEstrangeiro",
                    value
                  )
                }
                title="Cliente estrangeiro"
                description="E-mail, telefone e endereço brasileiro deixam de ser obrigatórios."
                icon={
                  <Globe2
                    size={20}
                    aria-hidden="true"
                  />
                }
              />
            </div>
          </div>
        </section>

        <section className="cform-card">
          <div className="cform-card__head">
            <span className="cform-card__icon cform-card__icon--cycle">
              <HeartHandshake
                size={20}
                aria-hidden="true"
              />
            </span>

            <div>
              <h2>
                Ciclo terapêutico
              </h2>
              <p>
                Registre o período de acompanhamento sem alterar automaticamente
                o status ativo do cliente.
              </p>
            </div>
          </div>

          <div className="cform-grid">
            <Field
              label="Início da terapia"
              error={
                errors.dataInicioTerapia
              }
              className="cform-span-6"
              hint="Opcional"
            >
              <input
                className="cform-input"
                type="date"
                value={
                  form.dataInicioTerapia
                }
                max={
                  form.dataFimTerapia ||
                  undefined
                }
                disabled={
                  cycleAlreadyEnded
                }
                onChange={(event) =>
                  setField(
                    "dataInicioTerapia",
                    event.target.value
                  )
                }
              />
            </Field>

            <Field
              label="Fim do ciclo terapêutico"
              error={
                errors.dataFimTerapia
              }
              className="cform-span-6"
              hint={
                cycleAlreadyEnded
                  ? "Ciclo já encerrado"
                  : "Deixe em branco enquanto estiver em acompanhamento"
              }
            >
              <div className="cform-date-control">
                <input
                  className="cform-input"
                  type="date"
                  value={
                    form.dataFimTerapia
                  }
                  min={
                    form.dataInicioTerapia ||
                    undefined
                  }
                  disabled={
                    cycleAlreadyEnded
                  }
                  onChange={(event) =>
                    setField(
                      "dataFimTerapia",
                      event.target.value
                    )
                  }
                />

                {form.dataFimTerapia &&
                !cycleAlreadyEnded ? (
                  <button
                    className="cform-date-clear"
                    type="button"
                    onClick={() =>
                      setField(
                        "dataFimTerapia",
                        ""
                      )
                    }
                    aria-label="Limpar data de fim do ciclo terapêutico"
                    title="Limpar data"
                  >
                    <X
                      size={16}
                      aria-hidden="true"
                    />
                    <span>Limpar</span>
                  </button>
                ) : null}
              </div>
            </Field>

            <div
              className={`cform-cycle-note cform-span-12 ${
                cycleAlreadyEnded
                  ? "cform-cycle-note--ended"
                  : ""
              }`}
            >
              <CalendarDays
                size={18}
                aria-hidden="true"
              />

              <div>
                <strong>
                  {cycleAlreadyEnded
                    ? "Ciclo terapêutico encerrado"
                    : "Encerramento com confirmação"}
                </strong>
                <span>
                  {cycleAlreadyEnded
                    ? "As datas do ciclo encerrado ficam protegidas contra alterações acidentais. O histórico clínico e financeiro permanece preservado."
                    : "Ao informar uma data de fim em um cliente existente, o sistema mostrará o impacto antes de confirmar. Agendamentos posteriores serão cancelados, seus lembretes serão interrompidos e a sincronização do Google Calendar será processada. O cadastro do cliente continuará ativo."}
                </span>

                {cycleAlreadyEnded ? (
                  <button
                    className="cform-cycle-new-btn"
                    type="button"
                    onClick={
                      openNewTherapyCycleDialog
                    }
                  >
                    <HeartHandshake
                      size={15}
                      aria-hidden="true"
                    />
                    Iniciar novo ciclo terapêutico
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </section>

        <section className="cform-card cform-card--couple">
          <div className="cform-card__head">
            <span className="cform-card__icon cform-card__icon--couple">
              <UsersRound
                size={20}
                aria-hidden="true"
              />
            </span>

            <div>
              <h2>
                Modalidade do acompanhamento
              </h2>
              <p>
                O cadastro permanece em nome da pessoa que procurou o atendimento.
                Ative a opção abaixo quando o acompanhamento envolver o casal.
              </p>
            </div>
          </div>

          <div className="cform-toggle-grid cform-toggle-grid--single">
            <ToggleCard
              checked={
                form.atendimentoCasal
              }
              onChange={
                setAtendimentoCasal
              }
              title="Atendimento de casal"
              description="Habilita os dados do parceiro(a) sem criar um segundo cadastro de cliente."
              icon={
                <HeartHandshake
                  size={20}
                  aria-hidden="true"
                />
              }
            />
          </div>

          {form.atendimentoCasal ? (
            <div className="cform-couple-panel">
              <div className="cform-couple-panel__intro">
                <div>
                  <strong>
                    Dados do parceiro(a)
                  </strong>
                  <span>
                    Nome e vínculo identificam a composição do atendimento.
                    O CPF é opcional.
                  </span>
                </div>

                <span className="cform-couple-panel__badge">
                  Casal
                </span>
              </div>

              <div className="cform-grid">
                <Field
                  label="Nome do parceiro(a)"
                  required
                  error={
                    errors.conjugeNome
                  }
                  className="cform-span-6"
                >
                  <div className="cform-input-icon">
                    <UserRound
                      size={17}
                      aria-hidden="true"
                    />

                    <input
                      value={
                        form?.conjuge?.nome ||
                        ""
                      }
                      onChange={(event) =>
                        setConjugeField(
                          "nome",
                          event.target.value
                        )
                      }
                      placeholder="Nome completo"
                      autoComplete="off"
                    />
                  </div>
                </Field>

                <Field
                  label="Vínculo"
                  required
                  error={
                    errors.conjugeTipo
                  }
                  className="cform-span-3"
                >
                  <select
                    className="cform-input cform-select"
                    value={
                      form?.conjuge?.tipo ||
                      ""
                    }
                    onChange={(event) =>
                      setConjugeField(
                        "tipo",
                        event.target.value
                      )
                    }
                  >
                    <option value="">
                      Selecione
                    </option>
                    <option value="marido">
                      Marido
                    </option>
                    <option value="esposa">
                      Esposa
                    </option>
                    <option value="companheiro">
                      Companheiro
                    </option>
                    <option value="companheira">
                      Companheira
                    </option>
                    <option value="outro">
                      Outro
                    </option>
                  </select>
                </Field>

                <Field
                  label="CPF do parceiro(a)"
                  hint="Opcional"
                  error={
                    errors.conjugeCpf
                  }
                  className="cform-span-3"
                >
                  <input
                    className="cform-input"
                    inputMode="numeric"
                    value={
                      form?.conjuge?.cpf ||
                      ""
                    }
                    onChange={(event) =>
                      setConjugeField(
                        "cpf",
                        formatCPF(
                          event.target.value
                        )
                      )
                    }
                    placeholder="000.000.000-00"
                  />
                </Field>
              </div>

              <div className="cform-couple-note">
                <ShieldCheck
                  size={17}
                  aria-hidden="true"
                />

                <span>
                  O parceiro(a) não é criado automaticamente como outro cliente.
                  O prontuário e o acompanhamento continuam vinculados ao cadastro principal.
                </span>
              </div>
            </div>
          ) : null}
        </section>

        <section className="cform-card">
          <div className="cform-card__head cform-card__head--address">
            <span className="cform-card__icon cform-card__icon--address">
              <MapPin
                size={20}
                aria-hidden="true"
              />
            </span>

            <div>
              <h2>
                Endereço
              </h2>
              <p>
                Informe o CEP para preencher automaticamente logradouro,
                bairro, cidade e UF. Para estrangeiros, o preenchimento é
                opcional.
              </p>
            </div>

            {!form.clienteEstrangeiro &&
            buscandoCep ? (
              <span className="cform-cep-status">
                <LoaderCircle
                  size={14}
                  className="cform-spin"
                  aria-hidden="true"
                />
                Consultando CEP
              </span>
            ) : !form.clienteEstrangeiro &&
              cepEncontrado ? (
              <span className="cform-cep-status cform-cep-status--success">
                <CheckCircle2
                  size={14}
                  aria-hidden="true"
                />
                Endereço localizado
              </span>
            ) : null}
          </div>

          <div className="cform-grid">
            <Field
              label="CEP"
              error={errors.cep}
              className="cform-span-3"
            >
              <input
                className="cform-input"
                type="text"
                inputMode="numeric"
                value={form.cep}
                onChange={(event) =>
                  setField(
                    "cep",
                    formatCEP(
                      event.target.value
                    )
                  )
                }
                placeholder="00000-000"
                autoComplete="postal-code"
                aria-busy={
                  buscandoCep
                }
              />
            </Field>

            <Field
              label="Endereço"
              required={
                !form.clienteEstrangeiro
              }
              error={errors.endereco}
              className="cform-span-9"
            >
              <input
                className="cform-input"
                value={form.endereco}
                onChange={(event) =>
                  setField(
                    "endereco",
                    event.target.value
                  )
                }
                placeholder="Rua, avenida, número e complemento"
                autoComplete="street-address"
              />
            </Field>

            <Field
              label="Bairro"
              required={
                !form.clienteEstrangeiro
              }
              error={errors.bairro}
              className="cform-span-4"
            >
              <input
                className="cform-input"
                value={form.bairro}
                onChange={(event) =>
                  setField(
                    "bairro",
                    event.target.value
                  )
                }
                placeholder="Bairro"
              />
            </Field>

            <Field
              label="Cidade"
              required={
                !form.clienteEstrangeiro
              }
              error={errors.cidade}
              className="cform-span-5"
            >
              <input
                className="cform-input"
                value={form.cidade}
                onChange={(event) =>
                  setField(
                    "cidade",
                    event.target.value
                  )
                }
                placeholder="Cidade"
                autoComplete="address-level2"
              />
            </Field>

            <Field
              label="UF"
              required={
                !form.clienteEstrangeiro
              }
              error={errors.estado}
              className="cform-span-3"
            >
              <input
                className="cform-input cform-input--uf"
                value={form.estado}
                maxLength={2}
                onChange={(event) =>
                  setField(
                    "estado",
                    event.target.value
                      .replace(
                        /[^a-zA-Z]/g,
                        ""
                      )
                      .toUpperCase()
                      .slice(0, 2)
                  )
                }
                placeholder="SP"
                autoComplete="address-level1"
              />
            </Field>
          </div>
        </section>

        <section className="cform-card">
          <div className="cform-card__head">
            <span className="cform-card__icon cform-card__icon--responsible">
              <ShieldCheck
                size={20}
                aria-hidden="true"
              />
            </span>

            <div>
              <h2>
                Responsáveis e pagamento
              </h2>
              <p>
                Identifique responsável legal e pagador quando forem pessoas
                diferentes do cliente.
              </p>
            </div>
          </div>

          <div className="cform-toggle-grid">
            <ToggleCard
              checked={
                form.menorDeIdade
              }
              onChange={(value) =>
                setField(
                  "menorDeIdade",
                  value
                )
              }
              title="Menor de idade"
              description="Habilita o CPF do responsável legal."
              icon={
                <UsersRound
                  size={20}
                  aria-hidden="true"
                />
              }
            />

            <ToggleCard
              checked={
                form.pagadorEhCliente
              }
              onChange={(value) =>
                setField(
                  "pagadorEhCliente",
                  value
                )
              }
              title="Cliente é o pagador"
              description="Quando desativado, permite informar CPF ou CNPJ do pagador."
              icon={
                <ContactRound
                  size={20}
                  aria-hidden="true"
                />
              }
            />
          </div>

          <div className="cform-grid cform-grid--spaced">
            {form.menorDeIdade ? (
              <Field
                label="CPF do responsável"
                error={
                  errors.cpfResponsavel
                }
                className="cform-span-6"
                hint="Opcional"
              >
                <input
                  className="cform-input"
                  inputMode="numeric"
                  value={
                    form.cpfResponsavel
                  }
                  onChange={(event) =>
                    setField(
                      "cpfResponsavel",
                      formatCPF(
                        event.target.value
                      )
                    )
                  }
                  placeholder="000.000.000-00"
                />
              </Field>
            ) : null}

            {!form.pagadorEhCliente ? (
              <Field
                label="CPF/CNPJ do pagador"
                error={
                  errors.cpfPagador
                }
                className="cform-span-6"
                hint="Opcional"
              >
                <input
                  className="cform-input"
                  inputMode="numeric"
                  value={
                    form.cpfPagador
                  }
                  onChange={(event) =>
                    setField(
                      "cpfPagador",
                      formatDocument(
                        event.target.value
                      )
                    )
                  }
                  placeholder="CPF ou CNPJ"
                />
              </Field>
            ) : null}

            {isEdit ? (
              <div className="cform-span-6 cform-toggle-wrap">
                <ToggleCard
                  checked={form.ativo}
                  onChange={(value) =>
                    setField(
                      "ativo",
                      value
                    )
                  }
                  title="Cliente ativo"
                  description="Controle cadastral independente do fim do ciclo terapêutico."
                  icon={
                    <ShieldCheck
                      size={20}
                      aria-hidden="true"
                    />
                  }
                />
              </div>
            ) : null}
          </div>
        </section>

        <section className="cform-card">
          <div className="cform-card__head cform-card__head--with-action">
            <div className="cform-card__head-copy">
              <span className="cform-card__icon cform-card__icon--emergency">
                <Phone
                  size={20}
                  aria-hidden="true"
                />
              </span>

              <div>
                <h2>
                  Contatos de emergência
                </h2>
                <p>
                  Cadastre uma ou mais pessoas para situações que exijam contato
                  externo.
                </p>
              </div>
            </div>

            <button
              type="button"
              className="cform-btn cform-btn--soft"
              onClick={addEmergencyContact}
            >
              <Plus
                size={16}
                aria-hidden="true"
              />
              Adicionar contato
            </button>
          </div>

          {errors.contatosEmergencia ? (
            <div className="cform-global-error">
              <AlertTriangle
                size={17}
                aria-hidden="true"
              />

              <span>
                {
                  errors.contatosEmergencia
                }
              </span>
            </div>
          ) : null}

          {form.contatosEmergencia.length ? (
            <div className="cform-emergency-list">
              {form.contatosEmergencia.map(
                (
                  contact,
                  index
                ) => (
                  <article
                    className="cform-emergency"
                    key={
                      contact.key
                    }
                  >
                    <header>
                      <div>
                        <span>
                          Contato{" "}
                          {index + 1}
                        </span>
                        <strong>
                          {contact.nome ||
                            "Novo contato"}
                        </strong>
                      </div>

                      <button
                        type="button"
                        onClick={() =>
                          removeEmergencyContact(
                            index
                          )
                        }
                        aria-label={`Remover contato ${
                          index + 1
                        }`}
                      >
                        <Trash2
                          size={16}
                          aria-hidden="true"
                        />
                      </button>
                    </header>

                    <div className="cform-grid">
                      <Field
                        label="Nome"
                        className="cform-span-4"
                      >
                        <input
                          className="cform-input"
                          value={
                            contact.nome
                          }
                          onChange={(event) =>
                            updateEmergencyContact(
                              index,
                              "nome",
                              event
                                .target
                                .value
                            )
                          }
                          placeholder="Nome"
                        />
                      </Field>

                      <Field
                        label="Telefone"
                        className="cform-span-4"
                      >
                        <input
                          className="cform-input"
                          inputMode="tel"
                          value={
                            contact.telefone
                          }
                          onChange={(event) =>
                            updateEmergencyContact(
                              index,
                              "telefone",
                              formatPhoneBR(
                                event
                                  .target
                                  .value
                              )
                            )
                          }
                          placeholder="(11) 99999-9999"
                        />
                      </Field>

                      <Field
                        label="Vínculo"
                        className="cform-span-4"
                      >
                        <input
                          className="cform-input"
                          value={
                            contact.parentesco
                          }
                          onChange={(event) =>
                            updateEmergencyContact(
                              index,
                              "parentesco",
                              event
                                .target
                                .value
                            )
                          }
                          placeholder="Ex.: mãe, cônjuge, irmão"
                        />
                      </Field>

                      <Field
                        label="E-mail"
                        className="cform-span-6"
                      >
                        <input
                          className="cform-input"
                          type="email"
                          value={
                            contact.email
                          }
                          onChange={(event) =>
                            updateEmergencyContact(
                              index,
                              "email",
                              event
                                .target
                                .value
                            )
                          }
                          placeholder="contato@email.com"
                        />
                      </Field>

                      <Field
                        label="Observação"
                        className="cform-span-6"
                      >
                        <input
                          className="cform-input"
                          value={
                            contact.observacao
                          }
                          onChange={(event) =>
                            updateEmergencyContact(
                              index,
                              "observacao",
                              event
                                .target
                                .value
                            )
                          }
                          placeholder="Informação adicional"
                        />
                      </Field>
                    </div>
                  </article>
                )
              )}
            </div>
          ) : (
            <div className="cform-empty">
              <Phone
                size={22}
                aria-hidden="true"
              />
              <strong>
                Nenhum contato cadastrado
              </strong>
              <span>
                O preenchimento é opcional e pode ser feito agora ou depois.
              </span>
            </div>
          )}
        </section>

        <section className="cform-card">
          <div className="cform-card__head">
            <span className="cform-card__icon cform-card__icon--notes">
              <HeartHandshake
                size={20}
                aria-hidden="true"
              />
            </span>

            <div>
              <h2>
                Observações cadastrais
              </h2>
              <p>
                Informações administrativas complementares sobre o cliente.
              </p>
            </div>
          </div>

          <Field
            label="Observações"
            hint="Evite registrar conteúdo clínico aqui; use prontuário/evolução."
          >
            <textarea
              className="cform-textarea"
              value={
                form.observacoes
              }
              onChange={(event) =>
                setField(
                  "observacoes",
                  event.target.value
                )
              }
              placeholder="Informações cadastrais adicionais..."
              rows={5}
            />
          </Field>
        </section>

        {!isEdit ? (
          <section className="cform-communication-note">
            <Mail
              size={19}
              aria-hidden="true"
            />

            <div>
              <strong>
                Comunicação após o cadastro
              </strong>
              <p>
                Se houver e-mail válido, o backend mantém o fluxo atual de envio
                do Termo de Compromisso. O frontend não chama Resend ou Twilio
                diretamente.
              </p>
            </div>
          </section>
        ) : null}

        <div className="cform-actions">
          <button
            className="cform-btn cform-btn--ghost"
            type="button"
            onClick={goBack}
            disabled={saving}
          >
            <ArrowLeft
              size={16}
              aria-hidden="true"
            />
            Cancelar
          </button>

          <button
            className="cform-btn cform-btn--primary"
            type="button"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <LoaderCircle
                  size={16}
                  className="cform-spin"
                  aria-hidden="true"
                />
                Salvando...
              </>
            ) : (
              <>
                <Save
                  size={16}
                  aria-hidden="true"
                />

                {isEdit
                  ? "Salvar alterações"
                  : "Cadastrar cliente"}
              </>
            )}
          </button>
        </div>
      </main>

      {cycleDialog ? (
        <div
          className="cform-modal-overlay"
          role="presentation"
          onMouseDown={(event) => {
            if (
              event.target ===
                event.currentTarget &&
              !cycleClosing
            ) {
              setCycleDialog(null);
              setCycleReason("");
            }
          }}
        >
          <section
            className="cform-cycle-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cycle-end-title"
          >
            <header className="cform-cycle-modal__head">
              <span className="cform-cycle-modal__icon">
                <AlertTriangle
                  size={22}
                  aria-hidden="true"
                />
              </span>

              <div>
                <span className="cform-cycle-modal__eyebrow">
                  Confirmação necessária
                </span>
                <h2 id="cycle-end-title">
                  Encerrar ciclo terapêutico?
                </h2>
                <p>
                  Revise o impacto antes de confirmar. Esta ação não exclui o cliente nem o prontuário.
                </p>
              </div>
            </header>

            <div className="cform-cycle-modal__date">
              <CalendarDays
                size={18}
                aria-hidden="true"
              />
              <span>
                Data de encerramento
              </span>
              <strong>
                {cycleDialog?.impacto
                  ?.dataFimTerapia
                  ? new Intl.DateTimeFormat(
                      "pt-BR",
                      { timeZone: "UTC" }
                    ).format(
                      new Date(
                        `${cycleDialog.impacto.dataFimTerapia}T12:00:00Z`
                      )
                    )
                  : "Não informada"}
              </strong>
            </div>

            <div className="cform-cycle-modal__impact">
              <div>
                <span>Agendamentos futuros</span>
                <strong>
                  {Number(
                    cycleDialog?.impacto
                      ?.agendamentosFuturos || 0
                  )}
                </strong>
                <small>Serão cancelados após a data informada.</small>
              </div>

              <div>
                <span>Google Calendar</span>
                <strong>
                  {Number(
                    cycleDialog?.impacto
                      ?.eventosGoogle || 0
                  )}
                </strong>
                <small>Eventos vinculados serão processados pela integração.</small>
              </div>

              <div>
                <span>Pendências financeiras</span>
                <strong>
                  {Number(
                    cycleDialog?.impacto
                      ?.cobrancasEmAbertoMantidas || 0
                  )}
                </strong>
                <small>Valores de sessões já realizadas serão preservados.</small>
              </div>
            </div>

            <div className="cform-cycle-modal__notice">
              <CheckCircle2
                size={18}
                aria-hidden="true"
              />
              <div>
                <strong>O que será preservado</strong>
                <span>
                  Prontuário, evoluções, sessões já realizadas, recibos, histórico financeiro e dados cadastrais permanecem intactos.
                </span>
              </div>
            </div>

            <Field
              label="Motivo / observação"
              hint="Opcional"
            >
              <textarea
                className="cform-textarea cform-cycle-modal__reason"
                value={cycleReason}
                maxLength={500}
                onChange={(event) =>
                  setCycleReason(
                    event.target.value
                  )
                }
                placeholder="Ex.: alta, encerramento acordado, pausa do acompanhamento..."
              />
            </Field>

            <footer className="cform-cycle-modal__actions">
              <button
                className="cform-btn cform-btn--ghost"
                type="button"
                disabled={cycleClosing}
                onClick={() => {
                  setCycleDialog(null);
                  setCycleReason("");
                }}
              >
                Voltar
              </button>

              <button
                className="cform-btn cform-btn--danger"
                type="button"
                disabled={cycleClosing}
                onClick={
                  confirmTherapyCycleEnd
                }
              >
                {cycleClosing ? (
                  <>
                    <LoaderCircle
                      size={16}
                      className="cform-spin"
                      aria-hidden="true"
                    />
                    Encerrando...
                  </>
                ) : (
                  <>
                    <CheckCircle2
                      size={16}
                      aria-hidden="true"
                    />
                    Confirmar encerramento
                  </>
                )}
              </button>
            </footer>
          </section>
        </div>
      ) : null}

      {newCycleDialog ? (
        <div
          className="cform-modal-overlay"
          role="presentation"
          onMouseDown={(event) => {
            if (
              event.target ===
                event.currentTarget &&
              !startingNewCycle
            ) {
              setNewCycleDialog(false);
            }
          }}
        >
          <section
            className="cform-cycle-modal cform-cycle-modal--new"
            role="dialog"
            aria-modal="true"
            aria-labelledby="new-cycle-title"
          >
            <header className="cform-cycle-modal__head">
              <span className="cform-cycle-modal__icon cform-cycle-modal__icon--new">
                <HeartHandshake
                  size={22}
                  aria-hidden="true"
                />
              </span>

              <div>
                <span className="cform-cycle-modal__eyebrow cform-cycle-modal__eyebrow--new">
                  Retorno ao acompanhamento
                </span>
                <h2 id="new-cycle-title">
                  Iniciar novo ciclo terapêutico
                </h2>
                <p>
                  O ciclo anterior continuará preservado no histórico. Agendamentos que foram cancelados no encerramento anterior não serão reativados automaticamente.
                </p>
              </div>
            </header>

            <div className="cform-cycle-modal__new-body">
              <Field
                label="Data de início do novo ciclo"
                required
              >
                <input
                  className="cform-input"
                  type="date"
                  min={
                    originalTherapyEnd ||
                    undefined
                  }
                  value={
                    newCycleStartDate
                  }
                  onChange={(event) =>
                    setNewCycleStartDate(
                      event.target.value
                    )
                  }
                />
              </Field>

              <div className="cform-cycle-modal__notice">
                <CheckCircle2
                  size={18}
                  aria-hidden="true"
                />
                <div>
                  <strong>Histórico preservado</strong>
                  <span>
                    O novo ciclo começa limpo para novos agendamentos, mantendo sessões, evoluções e financeiro do ciclo anterior.
                  </span>
                </div>
              </div>
            </div>

            <footer className="cform-cycle-modal__actions">
              <button
                className="cform-btn cform-btn--ghost"
                type="button"
                disabled={startingNewCycle}
                onClick={() =>
                  setNewCycleDialog(false)
                }
              >
                Voltar
              </button>

              <button
                className="cform-btn cform-btn--primary"
                type="button"
                disabled={
                  startingNewCycle ||
                  !newCycleStartDate
                }
                onClick={
                  confirmStartNewTherapyCycle
                }
              >
                {startingNewCycle ? (
                  <>
                    <LoaderCircle
                      size={16}
                      className="cform-spin"
                      aria-hidden="true"
                    />
                    Iniciando...
                  </>
                ) : (
                  <>
                    <HeartHandshake
                      size={16}
                      aria-hidden="true"
                    />
                    Confirmar novo ciclo
                  </>
                )}
              </button>
            </footer>
          </section>
        </div>
      ) : null}
    </div>
  );
}
